import React, { useState, useMemo, useEffect } from 'react'
import { values, sortBy, filter } from 'lodash-es'
import { connect, ConnectedProps } from 'react-redux'
import { bindActionCreators } from 'redux'
import * as filtersActions from 'syft-acp-core/store/filters/actions'
import { Store } from 'syft-acp-core/store'

import FilterHierarchyMultiselect from '../FilterHierarchyMultiselect'
import { Props, Option, ClientDeliveryTeam } from './types'

type AllProps = ConnectedProps<typeof storeConnector> & Props

const getDeliveryTeamOptions = (teamsMap: Record<string, ClientDeliveryTeam>): Option[] =>
  values(sortBy(filter(teamsMap, ['active', true]), ['id', 'name'])).map(team => ({
    value: String(team.id),
    label: team.name,
  }))

// Legacy parameter name for backwards compatibility with bookmarked URLs
const LEGACY_PARAM_NAME = 'client_delivery_team_id'

const getInitialValues = (query: Record<string, string | string[]>, name: string): Option[] => {
  // Read from new plural param (client_delivery_team_ids) and legacy singular param
  const newParamValue = query[name]
  const legacyParamValue = query[LEGACY_PARAM_NAME]

  // Parse new param values
  const newIds = newParamValue
    ? Array.isArray(newParamValue)
      ? newParamValue
      : newParamValue.split(',')
    : []

  // Parse legacy param value (single ID or comma-separated)
  const legacyIds = legacyParamValue
    ? Array.isArray(legacyParamValue)
      ? legacyParamValue
      : legacyParamValue.split(',')
    : []

  // Merge and deduplicate IDs (legacy first for consistency with backend)
  const allIds = [...new Set([...legacyIds, ...newIds])]

  return allIds.map(id => ({ value: String(id), label: '' }))
}

export const storeConnector = connect(
  (state: Store) => ({
    deliveryTeams: state.clientDeliveryTeams.entityMap,
    query: state.routing?.locationBeforeTransitions?.query || {},
  }),
  dispatch => ({
    actions: bindActionCreators(filtersActions, dispatch),
  }),
)

const FilterClientDeliveryTeamMultiselect = ({ deliveryTeams, actions, name, query, onChange }: AllProps) => {
  const teamOptions = useMemo(() => getDeliveryTeamOptions(deliveryTeams || {}), [deliveryTeams])

  // Get initial values from URL query params
  const initialValues = useMemo(() => {
    const queryValues = getInitialValues(query, name)
    // Map to include labels from teamOptions
    return queryValues
      .map(qv => {
        const option = teamOptions.find(opt => opt.value === qv.value)
        return option || null
      })
      .filter((opt): opt is Option => opt !== null)
  }, [query, name, teamOptions])

  const [selected, setSelected] = useState<Option[]>(initialValues)
  const [userHasInteracted, setUserHasInteracted] = useState(false)

  // Update selected when initialValues change (e.g., URL changes)
  // Only sync from URL when user hasn't interacted AND current selection is empty
  useEffect(() => {
    if (!userHasInteracted && initialValues.length > 0 && selected.length === 0) {
      setSelected(initialValues)
    }
  }, [initialValues, userHasInteracted, selected.length])

  const onChangeHandler = (val: Option[]) => {
    if (!userHasInteracted) {
      setUserHasInteracted(true)
    }
    setSelected(val)
    const ids = val.map(item => item.value)
    // Send array of IDs to the filter
    const filterValue = ids.length > 0 ? ids : null
    actions.setFilter(name, filterValue)
    onChange?.(name, filterValue || undefined)
  }

  return (
    <FilterHierarchyMultiselect
      values={selected}
      onChange={onChangeHandler}
      options={teamOptions}
      placeholder="Any"
      deselectOption
      scrollable
      refreshValues={false}
    />
  )
}

export default storeConnector(FilterClientDeliveryTeamMultiselect)
